import type {
  SessionEvent,
  PlayerEvent,
  GameDefinition,
  TeamPreset,
  PlayerGroup,
  SessionIndexEntry,
  ImageRecord,
} from '@/schemas';
import {
  parseGameDefinitionId,
  parseImageId,
  parsePlayerGroupId,
  parsePlayerId,
  parseSessionId,
  parseTeamId,
  parseUserId,
} from '@/schemas/ids';

export interface EventRow {
  id: string;
  session_id: string;
  type: string;
  payload: unknown;
  timestamp: number;
  user_id?: string;
  created_at?: string;
}

export interface PlayerEventRow {
  id: string;
  type: string;
  payload: unknown;
  timestamp: number;
  user_id?: string;
  created_at?: string;
}

export interface GameDefinitionRow {
  id: string;
  name: string;
  description: string | null;
  has_rounds: boolean;
  win_condition: string;
  round_target_points: number | null;
  game_target_points: number | null;
  target_rounds: number | null;
  team_scoring_type: string | null;
  version: number;
  user_id: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface TeamPresetRow {
  id: string;
  name: string | null;
  nickname: string | null;
  avatar: string | null;
  color: string | null;
  player_ids: unknown;
  user_id: string;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface PlayerGroupRow {
  id: string;
  name: string;
  description: string | null;
  player_ids: unknown;
  user_id: string;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
  avatar_image_id: string | null;
}

export interface SessionIndexRow {
  session_id: string;
  user_id: string;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface ImageRow {
  id: string;
  entity_type: string;
  entity_id: string;
  storage_path: string;
  thumbnail_path: string;
  caption: string | null;
  user_id: string;
  session_id: string | null;
  uploaded_at: string; // ISO timestamp string (timestamp with timezone in Supabase)
  created_at: string; // ISO timestamp string (timestamp with timezone in Supabase)
  removed_at: string | null; // ISO timestamp string (timestamp with timezone in Supabase)
}

export function encodeEventToRow(event: SessionEvent): EventRow {
  return {
    id: event.id,
    session_id: event.sessionId,
    type: event.type,
    payload: event.payload,
    timestamp: event.timestamp,
    user_id: event.userId ?? undefined,
  };
}

export function decodeRowToEvent(row: EventRow): SessionEvent {
  // Narrowing is done by downstream Zod validation before application.
  return {
    id: row.id,
    sessionId: row.session_id,
    timestamp: row.timestamp,
    type: row.type as SessionEvent['type'],
    payload: row.payload as SessionEvent['payload'],
    userId: row.user_id ?? undefined,
  } as SessionEvent;
}

export function encodePlayerEventToRow(event: PlayerEvent): PlayerEventRow {
  return {
    id: event.id,
    type: event.type,
    payload: event.payload,
    timestamp: event.timestamp,
    user_id: event.userId ?? undefined,
  };
}

export function decodeRowToPlayerEvent(row: PlayerEventRow): PlayerEvent {
  return {
    id: row.id,
    timestamp: row.timestamp,
    type: row.type as PlayerEvent['type'],
    payload: row.payload as PlayerEvent['payload'],
    userId: row.user_id ?? undefined,
  } as PlayerEvent;
}

export function encodeGameDefinitionToRow(game: GameDefinition): GameDefinitionRow {
  return {
    id: game.id,
    name: game.name,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    description: game.description ?? null,
    has_rounds: game.hasRounds,
    win_condition: game.winCondition,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    round_target_points: game.roundTargetPoints ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    game_target_points: game.gameTargetPoints ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    target_rounds: game.targetRounds ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    team_scoring_type: game.teamScoringType ?? null,
    version: game.version,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    user_id: game.userId ?? null,
    created_at: game.createdAt,
    updated_at: game.updatedAt,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    deleted_at: game.deletedAt ?? null,
  };
}

export function decodeRowToGameDefinition(row: GameDefinitionRow): GameDefinition {
  return {
    id: parseGameDefinitionId(row.id),
    name: row.name,
    description: row.description ?? undefined,
    hasRounds: row.has_rounds,
    winCondition: row.win_condition as GameDefinition['winCondition'],
    roundTargetPoints: row.round_target_points ?? undefined,
    gameTargetPoints: row.game_target_points ?? undefined,
    targetRounds: row.target_rounds ?? undefined,
    teamScoringType: (row.team_scoring_type as GameDefinition['teamScoringType']) ?? undefined,
    version: row.version,
    userId: row.user_id ? parseUserId(row.user_id) : undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at ?? undefined,
  };
}

export function encodeTeamPresetToRow(preset: TeamPreset): TeamPresetRow {
  return {
    id: preset.id,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    name: preset.name ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    nickname: preset.nickname ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    avatar: preset.avatarImageId ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    color: preset.color ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    player_ids: preset.playerIds ?? null,
    user_id: preset.userId ?? '', // Empty string for system presets (will be filtered by RLS)
    created_at: preset.createdAt,
    updated_at: preset.updatedAt,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    deleted_at: preset.deletedAt ?? null,
  };
}

export function decodeRowToTeamPreset(row: TeamPresetRow): TeamPreset {
  return {
    id: parseTeamId(row.id),
    name: row.name ?? undefined,
    nickname: row.nickname ?? undefined,
    avatarImageId: row.avatar ? parseImageId(row.avatar) : undefined,
    color: row.color ?? undefined,
    playerIds: Array.isArray(row.player_ids) ? row.player_ids : undefined,
    userId: row.user_id ? parseUserId(row.user_id) : undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at ?? undefined,
  };
}

export function encodePlayerGroupToRow(group: PlayerGroup): PlayerGroupRow {
  return {
    id: group.id,
    name: group.name,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    description: group.description ?? null,
    player_ids: group.playerIds,
    user_id: group.userId ?? '',
    created_at: group.createdAt,
    updated_at: group.updatedAt,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    deleted_at: group.deletedAt ?? null,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    avatar_image_id: group.avatarImageId ?? null,
  };
}

export function decodeRowToPlayerGroup(row: PlayerGroupRow): PlayerGroup {
  const playerIds = Array.isArray(row.player_ids)
    ? row.player_ids.map((id) => parsePlayerId(String(id)))
    : [];

  return {
    id: parsePlayerGroupId(row.id),
    name: row.name,
    description: row.description ?? undefined,
    playerIds,
    userId: row.user_id ? parseUserId(row.user_id) : undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at ?? undefined,
    avatarImageId: row.avatar_image_id ? parseImageId(row.avatar_image_id) : undefined,
  };
}

export function encodeSessionIndexToRow(entry: SessionIndexEntry): SessionIndexRow {
  return {
    session_id: entry.sessionId,
    user_id: entry.userId,
    created_at: entry.createdAt,
    updated_at: entry.updatedAt,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null for optional columns
    deleted_at: entry.deletedAt ?? null,
  };
}

export function decodeRowToSessionIndex(row: SessionIndexRow): SessionIndexEntry {
  return {
    sessionId: parseSessionId(row.session_id),
    userId: parseUserId(row.user_id),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at ?? undefined,
  };
}

export function encodeImageToRow(
  image: ImageRecord,
  storagePath: string,
  thumbnailPath: string,
): ImageRow {
  return {
    id: image.id,
    entity_type: image.entityType,
    entity_id: image.entityId,
    storage_path: storagePath,
    thumbnail_path: thumbnailPath,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    caption: image.caption ?? null,
    user_id: image.userId,
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    session_id: image.sessionId ?? null,
    uploaded_at: new Date(image.uploadedAt).toISOString(),
    created_at: new Date(image.createdAt).toISOString(),
    // eslint-disable-next-line unicorn/no-null -- Database schema requires null
    removed_at: image.deletedAt ? new Date(image.deletedAt).toISOString() : null,
  };
}

export function decodeRowToImage(
  row: ImageRow,
  remoteUrl: string,
  remoteThumbnailUrl: string,
): Omit<ImageRecord, 'localBlob' | 'localThumbnail'> {
  // Note: localBlob and localThumbnail are not stored in Supabase, only metadata
  // These need to be loaded from storage or kept locally
  return {
    id: parseImageId(row.id),
    entityType: row.entity_type as ImageRecord['entityType'],
    entityId: row.entity_id,
    remoteUrl,
    remoteThumbnailUrl,
    uploadStatus: 'synced' as const, // If it's in Supabase, it's synced
    caption: row.caption ?? undefined,
    uploadedAt: new Date(row.uploaded_at).getTime(), // Convert ISO to timestamp
    sessionId: row.session_id ? parseSessionId(row.session_id) : undefined,
    userId: parseUserId(row.user_id),
    createdAt: new Date(row.created_at).getTime(),
    updatedAt: new Date(row.created_at).getTime(), // Use created_at as updated_at fallback
    deletedAt: row.removed_at ? new Date(row.removed_at).getTime() : undefined,
  } as Omit<ImageRecord, 'localBlob' | 'localThumbnail'>;
}
